import { Injectable, inject } from '@angular/core';
import { LoggerService } from './logger.service';

export const UPDATE_RELOAD_KEY = 'app_update_reload_at';

/**
 * The one guarded full-page reload used to switch to a newly deployed version (navigation
 * intercept, `unrecoverable`, and the update banner's "Päivitä nyt").
 *
 * Guarded: if we already did this within the last 30 seconds then the reload is not
 * actually switching versions and something is re-triggering it, so stop rather than
 * loop. (The callers were previously unguarded, which is what let a reload loop run on
 * the signin callback.)
 */
@Injectable({ providedIn: 'root' })
export class AppReloadService {
  private readonly logger = inject(LoggerService);
  private reloadStarted = false;

  reloadForNewVersion(url: string): void {
    // location.assign() is asynchronous, so a second caller in this same page (e.g.
    // `unrecoverable` racing the navigation intercept) would otherwise read the
    // timestamp just written below and report a loop that isn't one.
    if (this.reloadStarted) return;
    try {
      const last = +(sessionStorage.getItem(UPDATE_RELOAD_KEY) ?? 0);
      if (last && Date.now() - last < 30_000) {
        this.logger.errorWithContext('AppComponent', 'Suppressed repeat update reload', url);
        return;
      }
      sessionStorage.setItem(UPDATE_RELOAD_KEY, String(Date.now()));
    } catch {
      // sessionStorage unavailable — proceed unguarded rather than strand the user
      // on a stale version.
    }
    this.reloadStarted = true;
    this.navigate(url.startsWith('/') ? url : '/');
  }

  /** Overridable seam for tests. */
  protected navigate(url: string): void {
    window.location.assign(url);
  }
}
